import React, { useCallback, useContext, useEffect, useState } from 'react'
import { AlertTriangle, CheckCircle, RefreshCw, Save, Settings, X } from 'lucide-react'
import { BASE_URL_API } from '../../References/urls'
import AuthContext from '../../store/auth-context'
import styles from './RewardConfigScreen.module.css'

// ─── UTILS ───────────────────────────────────────────────────────────────────
function formatDateTime(dateStr) {
    if (!dateStr) return '—'
    const d = new Date(String(dateStr).replace(' ', 'T'))
    if (Number.isNaN(d.getTime())) return '—'
    return d.toLocaleString('en-IN', {
        day: '2-digit', month: 'short', year: 'numeric',
        hour: '2-digit', minute: '2-digit', hour12: true,
    })
}

const GROUP_LABELS = {
    rewards: 'Rewards',
    withdrawal: 'Withdrawal',
    tiers: 'Tiers',
    operational: 'Operational',
}

// ─── TOGGLE SWITCH ───────────────────────────────────────────────────────────
function ToggleSwitch({ checked, onChange, disabled }) {
    return (
        <button
            type="button"
            role="switch"
            aria-checked={checked}
            disabled={disabled}
            className={`${styles.toggle} ${checked ? styles.toggleOn : styles.toggleOff}`}
            onClick={() => !disabled && onChange(!checked)}
        >
            <span className={styles.toggleThumb} />
        </button>
    )
}

// ─── CONFIRM SAVE MODAL ──────────────────────────────────────────────────────
function ConfirmSaveModal({ changedItems, onCancel, onConfirm, isSaving }) {
    return (
        <div className={styles.overlay}>
            <div className={styles.modal}>
                <div className={styles.modalHeader}>
                    <div className={styles.modalTitleRow}>
                        <AlertTriangle size={15} color="#d97706" />
                        <span className={styles.modalTitle}>Config Change Confirmation</span>
                    </div>
                    <button className={styles.iconBtn} onClick={onCancel} disabled={isSaving}>
                        <X size={13} />
                    </button>
                </div>
                <div className={styles.modalBody}>
                    <div className={styles.changesList}>
                        {changedItems.map(item => (
                            <div key={item.configKey} className={styles.changeRow}>
                                <div className={styles.changeLabel}>{item.configLabel}</div>
                                <div className={styles.changeValues}>
                                    <span className={styles.oldValue}>{item.oldValue}</span>
                                    <span className={styles.changeArrow}>→</span>
                                    <span className={styles.newValue}>{item.newValue}</span>
                                </div>
                            </div>
                        ))}
                    </div>
                    <p className={styles.confirmWarning}>
                        Yeh changes immediately live ho jaayengi. Kya aap sure hain?
                    </p>
                </div>
                <div className={styles.modalFooter}>
                    <button className={styles.btnConfirm} onClick={onConfirm} disabled={isSaving}>
                        {isSaving
                            ? <><RefreshCw size={12} className={styles.spinIcon} /> Saving...</>
                            : <><Save size={12} /> Haan, Save Karo</>
                        }
                    </button>
                    <button className={styles.btnSecondary} onClick={onCancel} disabled={isSaving}>Cancel</button>
                </div>
            </div>
        </div>
    )
}

// ─── CONFIG ITEM ─────────────────────────────────────────────────────────────
function ConfigItem({ item, editedValue, onChange, fieldError, recentlySaved }) {
    const isBoolean = item.configDataType === 'boolean'
    const isDecimal = item.configDataType === 'decimal'
    const isInteger = item.configDataType === 'integer'

    const currentValue = editedValue !== undefined ? editedValue : item.configValue
    const boolChecked = currentValue === 'true' || currentValue === true

    let placeholderText
    if (item.minValue && item.maxValue) placeholderText = `${item.minValue} – ${item.maxValue}`
    else if (item.minValue) placeholderText = `Min: ${item.minValue}`
    else if (item.maxValue) placeholderText = `Max: ${item.maxValue}`

    return (
        <div className={`${styles.configItem} ${!item.isActive ? styles.configItemInactive : ''} ${recentlySaved ? styles.configItemSaved : ''}`}>
            <div className={styles.configItemMain}>
                <div className={styles.configItemLeft}>
                    <div className={styles.configItemLabel}>
                        {item.configLabel}
                        {!item.isActive && <span className={styles.inactiveBadge}>Inactive</span>}
                    </div>
                    <div className={styles.configItemKey}>{item.configKey}</div>
                </div>
                <div className={styles.configItemControl}>
                    {isBoolean ? (
                        <div className={styles.toggleWrapper}>
                            <ToggleSwitch
                                checked={boolChecked}
                                onChange={v => onChange(v ? 'true' : 'false')}
                            />
                            <span className={styles.toggleLabel}>{boolChecked ? 'On' : 'Off'}</span>
                        </div>
                    ) : (
                        <input
                            type={isInteger || isDecimal ? 'number' : 'text'}
                            step={isDecimal ? '0.01' : isInteger ? '1' : undefined}
                            className={`${styles.configInput} ${fieldError ? styles.configInputError : ''}`}
                            value={currentValue}
                            onChange={e => onChange(e.target.value)}
                            placeholder={placeholderText}
                        />
                    )}
                    {recentlySaved && <CheckCircle size={16} color="#16a34a" className={styles.savedTick} />}
                </div>
            </div>

            {fieldError && (
                <div className={styles.fieldError}>
                    <AlertTriangle size={11} />
                    {fieldError}
                </div>
            )}

            {item.configDescription && (
                <div className={styles.configDescription}>{item.configDescription}</div>
            )}

            <div className={styles.configMeta}>
                {!isBoolean && (item.minValue || item.maxValue) && (
                    <span className={styles.metaConstraints}>
                        {item.minValue && `Min: ${item.minValue}`}
                        {item.minValue && item.maxValue && ' · '}
                        {item.maxValue && `Max: ${item.maxValue}`}
                    </span>
                )}
                {item.lastUpdatedOn && (
                    <span className={styles.metaUpdated}>
                        Updated {formatDateTime(item.lastUpdatedOn)} by {item.lastUpdatedBy}
                    </span>
                )}
            </div>
        </div>
    )
}

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────
const RewardConfigScreen = () => {
    const authCtx = useContext(AuthContext)
    const [config, setConfig] = useState({})
    const [groups, setGroups] = useState([])
    const [activeTab, setActiveTab] = useState('')
    const [isFetching, setIsFetching] = useState(false)
    const [editedValues, setEditedValues] = useState({})
    const [fieldErrors, setFieldErrors] = useState({})
    const [recentlySaved, setRecentlySaved] = useState(new Set())
    const [showConfirm, setShowConfirm] = useState(false)
    const [isSaving, setIsSaving] = useState(false)
    const [toast, setToast] = useState(null)
    const [unauthorizedError, setUnauthorizedError] = useState(false)

    const showToast = (message, type = 'success') => {
        setToast({ message, type })
        setTimeout(() => setToast(null), 3500)
    }

    const fetchConfig = useCallback(async () => {
        setIsFetching(true)
        try {
            const res = await fetch(BASE_URL_API + '/referralGetConfig', {
                method: 'POST',
                body: JSON.stringify({
                    token: authCtx.token,
                    adminId: authCtx.userIdentifier,
                    configGroup: '',
                }),
            })
            const data = await res.json()
            if (data.success === true) {
                const cfg = data.response?.config || {}
                const grps = data.response?.groups || Object.keys(cfg)
                setConfig(cfg)
                setGroups(grps)
                setEditedValues({})
                setFieldErrors({})
                setRecentlySaved(new Set())
            } else {
                if (data.message === 'token Expired, please login again!') authCtx.logout()
            }
        } catch (err) {
            console.error('fetchConfig error:', err)
        } finally {
            setIsFetching(false)
        }
    }, [authCtx.token, authCtx.userIdentifier])

    useEffect(() => { fetchConfig() }, [fetchConfig])

    useEffect(() => {
        if (!activeTab && groups.length > 0) setActiveTab(groups[0])
    }, [groups, activeTab])

    const handleFieldChange = (configKey, newValue) => {
        setEditedValues(prev => ({ ...prev, [configKey]: newValue }))
        setFieldErrors(prev => {
            const next = { ...prev }
            delete next[configKey]
            return next
        })
    }

    const getChangedItems = () =>
        groups.flatMap(g => config[g] || [])
            .filter(item => {
                const edited = editedValues[item.configKey]
                return edited !== undefined && String(edited) !== String(item.configValue)
            })
            .map(item => ({
                configKey: item.configKey,
                configLabel: item.configLabel,
                oldValue: item.configValue,
                newValue: editedValues[item.configKey],
            }))

    const changedItems = getChangedItems()
    const hasChanges = changedItems.length > 0

    const handleSaveAll = async () => {
        setShowConfirm(false)
        setIsSaving(true)
        setUnauthorizedError(false)
        const errors = { ...fieldErrors }
        const saved = new Set()

        for (const item of changedItems) {
            try {
                const res = await fetch(BASE_URL_API + '/referralUpdateConfig', {
                    method: 'POST',
                    body: JSON.stringify({
                        token: authCtx.token,
                        adminId: authCtx.userIdentifier,
                        configKey: item.configKey,
                        newValue: String(item.newValue),
                    }),
                })
                const data = await res.json()
                if (data.success === true) {
                    saved.add(item.configKey)
                    setEditedValues(prev => {
                        const next = { ...prev }
                        delete next[item.configKey]
                        return next
                    })
                    setConfig(prev => {
                        const next = { ...prev }
                        for (const grp of Object.keys(next)) {
                            next[grp] = next[grp].map(ci =>
                                ci.configKey === item.configKey
                                    ? {
                                        ...ci,
                                        configValue: item.newValue,
                                        lastUpdatedOn: data.response?.updatedOn || ci.lastUpdatedOn,
                                        lastUpdatedBy: data.response?.updatedBy || ci.lastUpdatedBy,
                                    }
                                    : ci
                            )
                        }
                        return next
                    })
                    showToast(`Saved: ${item.configLabel}`)
                } else {
                    const errType = data.response?.errorType || data.data?.errorType
                    if (errType === 'UNAUTHORIZED') {
                        setUnauthorizedError(true)
                        break
                    } else if (errType === 'BELOW_MIN') {
                        const min = data.response?.min || data.data?.min
                        errors[item.configKey] = min ? `Min value: ${min}` : data.message || 'Value too low'
                    } else if (errType === 'ABOVE_MAX') {
                        const max = data.response?.max || data.data?.max
                        errors[item.configKey] = max ? `Max value: ${max}` : data.message || 'Value too high'
                    } else {
                        errors[item.configKey] = data.message || 'Save failed'
                        showToast(`${item.configLabel} save nahi hua: ${data.message || 'Error'}`, 'error')
                        break
                    }
                }
            } catch {
                errors[item.configKey] = 'Network error'
                showToast(`Network error saving ${item.configLabel}`, 'error')
                break
            }
        }

        setFieldErrors(errors)
        setRecentlySaved(saved)
        setIsSaving(false)
        setTimeout(() => setRecentlySaved(new Set()), 3000)
    }

    const activeItems = config[activeTab] || []
    const isInitialLoad = isFetching && groups.length === 0

    if (unauthorizedError) {
        return (
            <div className={styles.mainWrapper}>
                <div className={styles.unauthorizedScreen}>
                    <AlertTriangle size={40} color="#dc2626" />
                    <div className={styles.unauthorizedTitle}>Aapko config change karne ki permission nahi hai.</div>
                </div>
            </div>
        )
    }

    return (
        <div className={styles.mainWrapper}>
            {showConfirm && (
                <ConfirmSaveModal
                    changedItems={changedItems}
                    onCancel={() => setShowConfirm(false)}
                    onConfirm={handleSaveAll}
                    isSaving={isSaving}
                />
            )}

            {toast && (
                <div className={`${styles.toast} ${toast.type === 'error' ? styles.toastError : styles.toastSuccess}`}>
                    {toast.type === 'error' ? <AlertTriangle size={13} /> : <CheckCircle size={13} />}
                    {toast.message}
                </div>
            )}

            {/* ── HEADER ─────────────────────────────────────────── */}
            <div className={styles.header}>
                <div className={styles.headerLeft}>
                    <Settings size={15} color="#6366f1" />
                    <span className={styles.pageTitle}>Reward Configuration</span>
                    {isFetching && <RefreshCw size={11} className={styles.spinIcon} />}
                </div>
                <div className={styles.headerRight}>
                    {hasChanges && (
                        <span className={styles.changesChip}>
                            {changedItems.length} unsaved {changedItems.length === 1 ? 'change' : 'changes'}
                        </span>
                    )}
                    {hasChanges && (
                        <button
                            className={styles.btnSave}
                            onClick={() => setShowConfirm(true)}
                            disabled={isSaving}
                        >
                            {isSaving
                                ? <><RefreshCw size={12} className={styles.spinIcon} /> Saving...</>
                                : <><Save size={12} /> Save Changes</>
                            }
                        </button>
                    )}
                </div>
            </div>

            {/* ── TABS ───────────────────────────────────────────── */}
            {groups.length > 0 && (
                <div className={styles.tabBar}>
                    {groups.map(grp => (
                        <button
                            key={grp}
                            className={`${styles.tab} ${activeTab === grp ? styles.tabActive : ''}`}
                            onClick={() => setActiveTab(grp)}
                        >
                            {GROUP_LABELS[grp] || grp}
                        </button>
                    ))}
                </div>
            )}

            {/* ── BODY ───────────────────────────────────────────── */}
            <div className={styles.body}>
                {isInitialLoad ? (
                    <div className={styles.loadingState}>
                        <RefreshCw size={22} className={styles.spinIcon} />
                        <span>Loading config...</span>
                    </div>
                ) : activeItems.length === 0 ? (
                    <div className={styles.emptyState}>
                        <Settings size={32} color="#d1d5db" />
                        <span>No config items for this group.</span>
                    </div>
                ) : (
                    <div className={styles.configList}>
                        {activeItems.map(item => (
                            <ConfigItem
                                key={item.configKey}
                                item={item}
                                editedValue={editedValues[item.configKey]}
                                onChange={v => handleFieldChange(item.configKey, v)}
                                fieldError={fieldErrors[item.configKey]}
                                recentlySaved={recentlySaved.has(item.configKey)}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    )
}

export default RewardConfigScreen
